import { DataDogLog } from './datadog';

/**
 * Utility to monitor and debug Datadog logging issues
 */
export class LogMonitor {
  private static instance: LogMonitor;
  private monitoringInterval: NodeJS.Timeout | null = null;

  static getInstance(): LogMonitor {
    if (!LogMonitor.instance) {
      LogMonitor.instance = new LogMonitor();
    }
    return LogMonitor.instance;
  }

  /**
   * Start monitoring the log buffer status
   * Useful for debugging rate limiting and batching issues
   */
  startMonitoring(intervalMs = 10000) {
    if (this.monitoringInterval) {
      this.stopMonitoring();
    }

    this.monitoringInterval = setInterval(() => {
      try {
        const status = DataDogLog.getBufferStatus();

        if (!status) {
          console.warn('DataDog buffer status is unavailable');
          return;
        }

        if (status.bufferedLogs > 50) {
          console.warn('DataDog log buffer is getting large:', status);
        }

        if (status.currentRateLimit >= status.maxRateLimit) {
          console.warn('DataDog logging is rate limited:', status);
        }

        // Log status in development
        if (process.env.NODE_ENV === 'development') {
          console.log('DataDog Buffer Status:', status);
        }
      } catch (error) {
        console.error('Error during log monitoring:', error);
      }
    }, intervalMs);
  }

  /**
   * Stop monitoring
   */
  stopMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
  }

  /**
   * Get current buffer status
   */
  getStatus() {
    return DataDogLog.getBufferStatus();
  }

  /**
   * Force flush logs (useful for debugging)
   */
  flushLogs() {
    DataDogLog.forceFlushBuffer();
  }

  /**
   * Add this to window for debugging in console
   */
  attachToWindow() {
    (window as any).logMonitor = {
      getStatus: () => this.getStatus(),
      flushLogs: () => this.flushLogs(),
      startMonitoring: (interval?: number) => this.startMonitoring(interval),
      stopMonitoring: () => this.stopMonitoring(),
    };
  }
}

// Auto-start monitoring in development
if (process.env.NODE_ENV === 'development') {
  const monitor = LogMonitor.getInstance();
  monitor.startMonitoring();
  monitor.attachToWindow();
}

export default LogMonitor;
